import { postSiteAnalytics } from "@/shared/api/siteAnalytics";
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { readVirtuozYclid, SITE_ANALYTICS_START_SESSION_KEY } from "./siteAnalyticsStorage";
import { resolveSiteMetrikaClientId } from "./yandexMetrikaTagLoader";
/**
 * Старт + виртуальные pageview в Метрику сайта через API (отдельный счётчик на бэке).
 *
 * Для НЕавторизованных пользователей бэк требует ycid. Если ycid ещё не
 * получен (counter не отдал getClientID) — запрос не шлём, иначе 422.
 * Для авторизованных бэк опознаёт пользователя по куке, ycid опционален.
 */
export function useServerSiteAnalytics(isAuthenticated) {
    const location = useLocation();
    const path = `${location.pathname}${location.search}`;
    const startOnceRef = useRef(false);
    const skipFirstPageviewRef = useRef(true);
    const debounceRef = useRef(null);
    // Актуальное значение авторизации для асинхронных колбэков ниже.
    const isAuthenticatedRef = useRef(isAuthenticated);
    isAuthenticatedRef.current = isAuthenticated;
    useEffect(() => {
        if (startOnceRef.current) {
            return;
        }
        startOnceRef.current = true;
        let already = false;
        try {
            already = sessionStorage.getItem(SITE_ANALYTICS_START_SESSION_KEY) === "1";
        }
        catch {
            /* ignore */
        }
        if (already) {
            return;
        }
        const yclid = readVirtuozYclid();
        void resolveSiteMetrikaClientId().then((ycid) => {
            // Неавторизованным без ycid не шлём (бэк требует ycid → 422).
            // start не помечаем отправленным — повторим на следующей загрузке,
            // когда ycid появится.
            if (!isAuthenticatedRef.current && !ycid) {
                return;
            }
            void postSiteAnalytics({
                ...(ycid ? { ycid } : {}),
                kind: "start",
                path: "/",
                title: typeof document !== "undefined" ? document.title : "",
                ...(yclid ? { yclid } : {}),
            }).then(() => {
                try {
                    sessionStorage.setItem(SITE_ANALYTICS_START_SESSION_KEY, "1");
                }
                catch {
                    /* ignore */
                }
            });
        });
    }, []);
    useEffect(() => {
        if (skipFirstPageviewRef.current) {
            skipFirstPageviewRef.current = false;
            return;
        }
        if (debounceRef.current !== null) {
            clearTimeout(debounceRef.current);
        }
        debounceRef.current = setTimeout(() => {
            const yclid = readVirtuozYclid();
            void resolveSiteMetrikaClientId().then((ycid) => {
                // Неавторизованным без ycid не шлём (бэк требует ycid → 422).
                if (!isAuthenticatedRef.current && !ycid) {
                    return;
                }
                void postSiteAnalytics({
                    ...(ycid ? { ycid } : {}),
                    kind: "pageview",
                    path,
                    title: typeof document !== "undefined" ? document.title : "",
                    ...(yclid ? { yclid } : {}),
                });
            });
        }, 450);
        return () => {
            if (debounceRef.current !== null) {
                clearTimeout(debounceRef.current);
                debounceRef.current = null;
            }
        };
    }, [path]);
}
